import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import CarCard from "../components/CarCard";
import { Button } from "../components/ui/button";
import { ArrowRight, GitCompareArrows, Loader2, Swords, Sparkles, CheckCircle2, AlertCircle, BarChart3, BookOpen, Network } from "lucide-react";

// Path slug → "vergleich" (DE), "comparaison" (FR), etc. Keeps URLs locale-native.
const ROUTE_SLUG_BY_LOCALE = {
  de: "vergleich",
  fr: "comparaison",
  it: "confronto",
  en: "compare",
};

function StructuredData({ data }) {
  // Renders a JSON-LD `<script>` element that browsers won't parse as JS.
  useEffect(() => {
    if (!data) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.compareSeo = "1";
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
    return () => {
      document.querySelectorAll('script[data-compare-seo="1"]').forEach((n) => n.remove());
    };
  }, [data]);
  return null;
}

function setMetaTag(selector, content) {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkTag(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="alternate"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function useSeoHead({ data, slug, locale }) {
  useEffect(() => {
    if (!data?.ok) return;
    const title = `${data.hero.h1} · CarHive`;
    document.title = title;
    setMetaTag('meta[name="description"]', data.hero.subtitle);
    // OpenGraph
    setMetaTag('meta[property="og:title"]', data.hero.h1);
    setMetaTag('meta[property="og:description"]', data.hero.subtitle);
    setMetaTag('meta[property="og:type"]', "website");
    setMetaTag('meta[property="og:site_name"]', "CarHive");
    const firstImg = (data.listings_a?.[0]?.images?.[0]) || (data.listings_b?.[0]?.images?.[0]);
    if (firstImg) setMetaTag('meta[property="og:image"]', api.fileUrl(firstImg));
    // Twitter
    setMetaTag('meta[name="twitter:card"]', "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', data.hero.h1);
    setMetaTag('meta[name="twitter:description"]', data.hero.subtitle);
    // hreflang alternates — point each locale's path to the same canonical slug
    const origin = window.location.origin;
    Object.entries(ROUTE_SLUG_BY_LOCALE).forEach(([lc, path]) => {
      setLinkTag("alternate", `${origin}/${lc}/${path}/${data.slug}`, lc);
    });
    setLinkTag("alternate", `${origin}/de/vergleich/${data.slug}`, "x-default");
    // canonical for current locale
    const currentPath = ROUTE_SLUG_BY_LOCALE[locale] || "vergleich";
    setLinkTag("canonical", `${origin}/${locale}/${currentPath}/${data.slug}`);
  }, [data, slug, locale]);
}

function buildItemListJsonLd(data) {
  if (!data?.ok) return null;
  const items = [...(data.listings_a || []), ...(data.listings_b || [])];
  if (items.length === 0) return null;
  const origin = (typeof window !== "undefined") ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": data.hero.h1,
    "description": data.hero.subtitle,
    "numberOfItems": items.length,
    "itemListElement": items.map((it, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "item": {
        "@type": "Product",
        "name": `${it.brand} ${it.model}`.trim(),
        "url": `${origin}/${data.locale}/listings/${it.id}`,
        "image": (it.images || []).slice(0, 1).map((p) => api.fileUrl(p)),
        "offers": it.price_chf != null ? {
          "@type": "Offer",
          "priceCurrency": "CHF",
          "price": it.price_chf,
          "availability": "https://schema.org/InStock",
        } : undefined,
      },
    })),
  };
}

function buildFaqJsonLd(faq) {
  if (!faq || faq.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
}

function BrandStatChip({ label, value, accent }) {
  return (
    <div className={`rounded-lg border bg-zinc-950 px-4 py-3 ${accent}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <p className="mt-0.5 font-display text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

/** 5-segment score bar (matches our 1–5 scoring on the backend). */
function ScoreRow({ label, score, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 truncate text-[11px] text-zinc-400">{label}</span>
      <div className="flex flex-1 gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={`h-1.5 flex-1 rounded-sm ${i <= score ? color : "bg-zinc-800"}`} />
        ))}
      </div>
      <span className="w-6 text-right text-[10px] tabular-nums text-zinc-500">{score}/5</span>
    </div>
  );
}

function BrandTraits({ brand, strengths, weaknesses, accent }) {
  const accents = {
    red: "border-red-500/30 bg-red-950/20",
    sky: "border-sky-500/30 bg-sky-950/20",
  };
  return (
    <div className={`rounded-lg border ${accents[accent] || accents.red} p-4`} data-testid={`compare-seo-traits-${accent}`}>
      <p className="font-display text-base font-semibold text-white">{brand}</p>
      {(strengths || []).length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">Stärken</p>
          <ul className="mt-1.5 space-y-1">
            {strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[13px] leading-snug text-zinc-200">
                <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-400" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(weaknesses || []).length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-300">Schwächen</p>
          <ul className="mt-1.5 space-y-1">
            {weaknesses.map((w, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[13px] leading-snug text-zinc-300">
                <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-amber-400" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CompareSeoPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    api.compareSeoPage(slug, lang)
      .then((r) => {
        if (!r?.ok) {
          setError(r?.reason || "not_found");
          setData(null);
        } else {
          setData(r);
        }
      })
      .catch(() => setError("network"))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  useSeoHead({ data, slug, locale: lang });
  const itemListLd = useMemo(() => buildItemListJsonLd(data), [data]);
  const faqLd = useMemo(() => buildFaqJsonLd(data?.faq), [data]);

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-24 text-sm text-zinc-500 sm:px-6" data-testid="compare-seo-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Lade…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6" data-testid="compare-seo-empty">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">404</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-white">Vergleich nicht gefunden</h1>
        <p className="mt-3 text-sm text-zinc-400">Diese Marken-Kombination ist (noch) nicht verfügbar. Schau dir alle Fahrzeuge auf CarHive an.</p>
        <Button asChild className="mt-6 bg-red-600 hover:bg-red-500" data-testid="compare-seo-back-btn">
          <Link to="/listings">Alle Fahrzeuge</Link>
        </Button>
      </div>
    );
  }

  const { brand_a, brand_b, brand_a_slug, brand_b_slug, hero, labels, listings_a, listings_b, models_a, models_b, counts, compare_volume, faq, buying_guide: guide, market_insights: insights, strengths_matrix: matrix, related_pairs } = data;
  const compareIds = [...(listings_a || []).slice(0, 1), ...(listings_b || []).slice(0, 1)]
    .map((c) => c.id)
    .filter(Boolean);
  const compareHref = compareIds.length === 2
    ? `/compare?${compareIds.map((id) => `id=${encodeURIComponent(id)}`).join("&")}`
    : "/compare";

  return (
    <article className="mx-auto max-w-7xl px-4 pb-20 pt-10 sm:px-6 lg:px-8" data-testid="compare-seo-page">
      <StructuredData data={itemListLd} />
      <StructuredData data={faqLd} />

      {/* Hero */}
      <header className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-red-950/40 via-zinc-950 to-zinc-950 px-5 py-10 sm:px-10 sm:py-14" data-testid="compare-seo-hero">
        <div className="flex items-center gap-2">
          <Swords className="h-4 w-4 text-red-500" />
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-500">{brand_a} · {brand_b}</p>
        </div>
        <h1 className="mt-3 font-display text-3xl font-bold leading-tight text-white sm:text-5xl" data-testid="compare-seo-h1">{hero.h1}</h1>
        <p className="mt-3 max-w-2xl text-sm text-zinc-300 sm:text-base">{hero.subtitle}</p>
        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">{hero.intro}</p>

        {/* Stat row */}
        <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <BrandStatChip label={brand_a} value={`${counts.a} Inserate`} accent="border-zinc-800" />
          <BrandStatChip label={brand_b} value={`${counts.b} Inserate`} accent="border-zinc-800" />
          <BrandStatChip label="30T Compares" value={compare_volume.pair_count} accent="border-zinc-800" />
          <BrandStatChip label={brand_a + " Pop."} value={`${compare_volume.a_popularity}× / ${compare_volume.b_popularity}×`} accent="border-zinc-800" />
        </div>

        <div className="mt-7 flex flex-wrap gap-2">
          <Button asChild className="bg-red-600 hover:bg-red-500" data-testid="compare-seo-cta-compare">
            <Link to={compareHref}><GitCompareArrows className="mr-1.5 h-4 w-4" /> {labels.compare_cta}</Link>
          </Button>
          <Button asChild variant="outline" className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
            <Link to={`/listings?brand=${encodeURIComponent(brand_a)}`} data-testid="compare-seo-link-brand-a">{labels.view_all_a} <ArrowRight className="ml-1.5 h-3 w-3" /></Link>
          </Button>
          <Button asChild variant="outline" className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
            <Link to={`/listings?brand=${encodeURIComponent(brand_b)}`} data-testid="compare-seo-link-brand-b">{labels.view_all_b} <ArrowRight className="ml-1.5 h-3 w-3" /></Link>
          </Button>
        </div>
      </header>

      {/* Brand A listings */}
      <section className="mt-12" data-testid="compare-seo-section-a">
        <div className="flex items-end justify-between gap-3">
          <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">{labels.section_a}</h2>
          <Link to={`/listings?brand=${encodeURIComponent(brand_a)}`} className="hidden text-xs text-zinc-400 hover:text-red-400 sm:inline-flex sm:items-center sm:gap-1">
            {labels.view_all_a} <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {listings_a.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-500" data-testid="compare-seo-empty-a">{labels.no_data}</p>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {listings_a.map((it) => <CarCard key={it.id} car={it} />)}
          </div>
        )}
      </section>

      {/* Brand B listings */}
      <section className="mt-14" data-testid="compare-seo-section-b">
        <div className="flex items-end justify-between gap-3">
          <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">{labels.section_b}</h2>
          <Link to={`/listings?brand=${encodeURIComponent(brand_b)}`} className="hidden text-xs text-zinc-400 hover:text-red-400 sm:inline-flex sm:items-center sm:gap-1">
            {labels.view_all_b} <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {listings_b.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-500" data-testid="compare-seo-empty-b">{labels.no_data}</p>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {listings_b.map((it) => <CarCard key={it.id} car={it} />)}
          </div>
        )}
      </section>

      {/* Buying Guide — Phase 50.1 (editorial long-form content for SEO depth) */}
      {guide && guide.length > 0 && (
        <section className="mt-14 rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:p-7" data-testid="compare-seo-buying-guide">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-red-500" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-500">Kaufberatung</p>
          </div>
          <div className="mt-3 space-y-6">
            {guide.map((sec, i) => (
              <div key={i} data-testid={`compare-seo-guide-${i}`}>
                <h2 className="font-display text-lg font-semibold text-white sm:text-xl">{sec.heading}</h2>
                <div className="mt-2 space-y-3">
                  {sec.paragraphs.map((p, j) => (
                    <p key={j} className="text-sm leading-relaxed text-zinc-300">{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Market Insights — Switzerland-specific data */}
      {insights && (
        <section className="mt-10 rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:p-7" data-testid="compare-seo-market-insights">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-red-500" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-500">Schweizer Markt</p>
          </div>
          <h2 className="mt-2 font-display text-lg font-semibold text-white sm:text-xl">{insights.heading}</h2>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <BrandStatChip label={`Ø Preis ${brand_a}`} value={insights.stats_a?.avg_price ? `CHF ${new Intl.NumberFormat('de-CH').format(insights.stats_a.avg_price)}` : "—"} accent="border-zinc-800" />
            <BrandStatChip label={`Ø Preis ${brand_b}`} value={insights.stats_b?.avg_price ? `CHF ${new Intl.NumberFormat('de-CH').format(insights.stats_b.avg_price)}` : "—"} accent="border-zinc-800" />
            <BrandStatChip label={`Ø km ${brand_a}`} value={insights.stats_a?.avg_mileage ? `${new Intl.NumberFormat('de-CH').format(insights.stats_a.avg_mileage)} km` : "—"} accent="border-zinc-800" />
            <BrandStatChip label={`Ø km ${brand_b}`} value={insights.stats_b?.avg_mileage ? `${new Intl.NumberFormat('de-CH').format(insights.stats_b.avg_mileage)} km` : "—"} accent="border-zinc-800" />
          </div>
          <div className="mt-4 space-y-3">
            {(insights.paragraphs || []).map((p, i) => (
              <p key={i} className="text-sm leading-relaxed text-zinc-300">{p}</p>
            ))}
          </div>
        </section>
      )}

      {/* Strengths & Weaknesses Matrix */}
      {matrix && (
        <section className="mt-10 rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:p-7" data-testid="compare-seo-strengths-matrix">
          <div className="flex items-center gap-2">
            <Swords className="h-4 w-4 text-red-500" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-500">Direktvergleich</p>
          </div>
          <h2 className="mt-2 font-display text-lg font-semibold text-white sm:text-xl">{matrix.heading}</h2>
          <p className="mt-2 text-sm leading-relaxed text-zinc-300">{matrix.summary}</p>

          {/* Dimensions: 2 score-bars per row */}
          <div className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {(matrix.dimensions || []).map((d) => (
              <div key={d.key} className="rounded-md border border-zinc-800/70 bg-black/40 p-3" data-testid={`compare-seo-dim-${d.key}`}>
                <div className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                  <span>{d.label}</span>
                  {d.leader === "a" && <span className="rounded bg-red-600/15 px-1.5 py-0.5 text-[10px] text-red-300">{brand_a}</span>}
                  {d.leader === "b" && <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] text-sky-300">{brand_b}</span>}
                  {d.leader === "tie" && <span className="rounded bg-zinc-700/40 px-1.5 py-0.5 text-[10px] text-zinc-300">unentschieden</span>}
                </div>
                <div className="mt-2 space-y-1.5">
                  <ScoreRow label={brand_a} score={d.score_a} color={d.leader === "a" ? "bg-red-600" : "bg-zinc-700"} />
                  <ScoreRow label={brand_b} score={d.score_b} color={d.leader === "b" ? "bg-sky-500" : "bg-zinc-700"} />
                </div>
              </div>
            ))}
          </div>

          {/* Strengths & weaknesses lists */}
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BrandTraits brand={brand_a} strengths={matrix.strengths_a} weaknesses={matrix.weaknesses_a} accent="red" />
            <BrandTraits brand={brand_b} strengths={matrix.strengths_b} weaknesses={matrix.weaknesses_b} accent="sky" />
          </div>
        </section>
      )}

      {/* Popular models (existing) */}
      {(models_a.length > 0 || models_b.length > 0) && (
        <section className="mt-14 grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="compare-seo-popular-models">
          <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{labels.popular_models} · {brand_a}</p>
            <ul className="mt-3 space-y-1.5">
              {models_a.map((m) => (
                <li key={m.model}>
                  <Link
                    to={`/listings?brand=${encodeURIComponent(brand_a)}&model=${encodeURIComponent(m.model)}`}
                    className="group flex items-center justify-between rounded-md px-2 py-1.5 text-sm text-zinc-200 transition hover:bg-zinc-900 hover:text-red-300"
                  >
                    <span className="flex items-center gap-2"><Sparkles className="h-3 w-3 text-red-500/70" />{brand_a} {m.model}</span>
                    <span className="text-[11px] text-zinc-500 group-hover:text-zinc-400">{m.count} Inserate</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{labels.popular_models} · {brand_b}</p>
            <ul className="mt-3 space-y-1.5">
              {models_b.map((m) => (
                <li key={m.model}>
                  <Link
                    to={`/listings?brand=${encodeURIComponent(brand_b)}&model=${encodeURIComponent(m.model)}`}
                    className="group flex items-center justify-between rounded-md px-2 py-1.5 text-sm text-zinc-200 transition hover:bg-zinc-900 hover:text-red-300"
                  >
                    <span className="flex items-center gap-2"><Sparkles className="h-3 w-3 text-red-500/70" />{brand_b} {m.model}</span>
                    <span className="text-[11px] text-zinc-500 group-hover:text-zinc-400">{m.count} Inserate</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq && faq.length > 0 && (
        <section className="mt-14 rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:p-7" data-testid="compare-seo-faq">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">FAQ</p>
          <div className="mt-4 divide-y divide-zinc-900">
            {faq.map((f, i) => (
              <details key={i} className="group py-3" data-testid={`compare-seo-faq-item-${i}`}>
                <summary className="cursor-pointer list-none text-base font-semibold text-white">
                  <span className="mr-2 text-red-500">›</span>{f.q}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-zinc-300">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Related comparisons — internal linking for SEO topic-cluster */}
      {related_pairs && related_pairs.length > 0 && (
        <section className="mt-10 rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:p-7" data-testid="compare-seo-related-pairs">
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-red-500" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-500">Verwandte Vergleiche</p>
          </div>
          <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {related_pairs.map((rp) => (
              <li key={rp.slug}>
                <Link
                  to={`/vergleich/${rp.slug}`}
                  className="group flex items-center justify-between rounded-md border border-zinc-800/70 bg-black/40 px-3 py-2.5 text-sm text-zinc-200 transition hover:border-red-500/40 hover:text-red-300"
                  data-testid={`compare-seo-related-${rp.slug}`}
                >
                  <span className="flex items-center gap-2"><GitCompareArrows className="h-3.5 w-3.5 text-zinc-500 group-hover:text-red-400" />{rp.brand_a} vs {rp.brand_b}</span>
                  <ArrowRight className="h-3 w-3 text-zinc-600 group-hover:text-red-400" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Back nav */}
      <footer className="mt-14 flex flex-wrap justify-between gap-3 border-t border-zinc-900 pt-6 text-sm text-zinc-400">
        <Link to="/listings" className="hover:text-red-300">← {labels.back_to_listings}</Link>
        <div className="flex flex-wrap gap-3 text-[12px] text-zinc-500">
          <Link to={`/listings?brand=${encodeURIComponent(brand_a)}`} className="hover:text-red-300">{brand_a}</Link>
          <span aria-hidden="true">·</span>
          <Link to={`/listings?brand=${encodeURIComponent(brand_b)}`} className="hover:text-red-300">{brand_b}</Link>
          <span aria-hidden="true">·</span>
          <Link to={`/vergleich/${brand_b_slug}-vs-${brand_a_slug}`} className="hover:text-red-300">{brand_b} vs {brand_a}</Link>
        </div>
      </footer>
    </article>
  );
}
